import { z } from "zod";
import { customFieldsCopy } from "@/features/custom-fields/custom-fields-copy";
import { duplicateKeyRefusal, firstDuplicateKey } from "@/domain/custom-fields/duplicate-keys";
import { forbiddenTopicIn } from "@/domain/custom-fields/forbidden-topics";
import { customFieldKeyFromLabel } from "@/domain/custom-fields/key";
import {
  customFieldsLimit,
  customTextFieldDefaultMaxLength,
  type CustomFieldDefinition,
  type CustomFieldType,
} from "@/domain/custom-fields/types";

export const customFieldLabelMaxLength = 60;

/** One row of the field editor. Numeric bounds stay strings while the partner
 *  types; `isSaved` marks a row that came back from the database, whose key is
 *  frozen because answers may already be filed under it. */
export interface CustomFieldRow {
  key: string;
  label: string;
  type: CustomFieldType;
  required: boolean;
  maxLength: string;
  min: string;
  max: string;
  isSaved: boolean;
}

const customFieldRowSchema = z.object({
  key: z.string(),
  label: z.string(),
  type: z.enum(["text", "number"]),
  required: z.boolean(),
  maxLength: z.string(),
  min: z.string(),
  max: z.string(),
  isSaved: z.boolean(),
});

/** Row problems are reported on the array itself, numbered as the partner sees
 *  them: react-hook-form shows no message for an error nested in an array item. */
export const customFieldRowsSchema = z
  .array(customFieldRowSchema)
  .max(customFieldsLimit, `Můžete přidat nejvýše ${customFieldsLimit} doplňujících údajů`)
  .superRefine((rows, ctx) => {
    rows.forEach((row, index) => {
      const problem = rowProblem(row);
      if (problem)
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `${customFieldsCopy.fieldOrdinal(index + 1)}: ${problem}`,
        });
    });

    const repeated = firstDuplicateKey(rows.map((row) => row.key));
    if (repeated === null) return;
    const index = rows.map((row) => row.key).lastIndexOf(repeated);
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `${customFieldsCopy.fieldOrdinal(index + 1)}: ${duplicateKeyRefusal(rows[index].label.trim() || repeated)}`,
    });
  });

function rowProblem(row: CustomFieldRow): string | null {
  if (!row.label.trim()) return "zadejte popisek";
  const forbiddenTopic = forbiddenTopicIn(row.label);
  if (forbiddenTopic) return customFieldsCopy.forbiddenTopicRefusal[forbiddenTopic];
  if (row.label.trim().length > customFieldLabelMaxLength)
    return `popisek smí mít nejvýše ${customFieldLabelMaxLength} znaků`;
  if (row.type === "number") {
    const min = parseBound(row.min);
    const max = parseBound(row.max);
    if (min === "invalid" || max === "invalid") return "zadejte rozsah jako celé číslo";
    if (min !== null && max !== null && min > max) return "minimum nesmí být větší než maximum";
    return null;
  }
  const maxLength = parseBound(row.maxLength);
  if (maxLength === "invalid") return "zadejte délku jako celé číslo";
  if (maxLength !== null && (maxLength < 1 || maxLength > 500)) return "délka musí být 1 až 500 znaků";
  return null;
}

export function newCustomFieldRow(): CustomFieldRow {
  return {
    key: "",
    label: "",
    type: "text",
    required: false,
    maxLength: "",
    min: "",
    max: "",
    isSaved: false,
  };
}

export function definitionsToRows(definitions: CustomFieldDefinition[] | null): CustomFieldRow[] {
  return (definitions ?? []).map((definition) => ({
    ...newCustomFieldRow(),
    key: definition.key,
    label: definition.label,
    type: definition.type,
    required: definition.required,
    maxLength: definition.type === "text" ? String(definition.maxLength) : "",
    min: definition.type === "number" ? boundToInput(definition.min) : "",
    max: definition.type === "number" ? boundToInput(definition.max) : "",
    isSaved: true,
  }));
}

/** Copying the activity's questions onto an offer starts a fresh override:
 *  nobody has answered them on this termín, so their keys still follow the label. */
export function definitionsToNewRows(definitions: CustomFieldDefinition[] | null): CustomFieldRow[] {
  return definitionsToRows(definitions).map((row) => ({ ...row, isSaved: false }));
}

export function rowsToDefinitions(rows: CustomFieldRow[]): CustomFieldDefinition[] {
  return rows.map((row) => {
    const shared = { key: row.key, label: row.label.trim(), required: row.required };
    if (row.type === "number") {
      return { ...shared, type: "number", min: numericBound(row.min), max: numericBound(row.max) };
    }
    return {
      ...shared,
      type: "text",
      maxLength: numericBound(row.maxLength) ?? customTextFieldDefaultMaxLength,
    };
  });
}

/** Renaming a label re-keys only a row nobody has answered yet — a saved row
 *  keeps its key so past answers stay filed where they were written. */
export function withLabel(rows: CustomFieldRow[], index: number, label: string): CustomFieldRow[] {
  return rows.map((row, i) => {
    if (i !== index) return row;
    if (row.isSaved) return { ...row, label };
    const siblingKeys = rows.filter((_, other) => other !== index).map((sibling) => sibling.key);
    return { ...row, label, key: customFieldKeyFromLabel(label.trim(), siblingKeys) };
  });
}

function numericBound(value: string): number | null {
  const bound = parseBound(value);
  return bound === "invalid" || bound === null ? null : bound;
}

function parseBound(value: string): number | null | "invalid" {
  if (!value.trim()) return null;
  return /^-?\d+$/.test(value.trim()) ? Number(value) : "invalid";
}

function boundToInput(bound: number | null): string {
  return bound === null ? "" : String(bound);
}
